import { defineStore } from "pinia";
import axios from "@/axios";
import Swal from "sweetalert2";

function jsendUnwrap(res) {
  const payload = res?.data;

  if (!payload || typeof payload !== "object" || !("status" in payload)) {
    return payload;
  }

  if (payload.status === "success") return payload.data;

  const msg = payload.message || "Operación fallida";
  const err = new Error(msg);
  err.jsend = payload;
  throw err;
}

function normalizeLigaId(value) {
  const ligaId = Number(value);

  if (!Number.isInteger(ligaId) || ligaId <= 0) {
    return null;
  }

  return ligaId;
}

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.data?.message ||
    error?.response?.data?.data ||
    error?.jsend?.message ||
    error?.message ||
    fallback
  );
}

export const useGameStore = defineStore("game", {
  state: () => ({
    CURRENT_PLAYER_ID: null,

    activeTab: "areas",
    loading: false,
    error: null,
    authToken: null,
    authUser: null,
    systemBlocked: false,
    // Estado global del procesamiento del demonio
    systemMessage: null,
    systemProcess: null,
    systemStatusChecking: false,

    budget: 0,
    reservedBudget: 0,
    portfolioValue: 0,
    creditLimit: 0,
    spendingCapacity: 0,
    inDebt: false,
    currentDay: 0,
    totalDays: 0,
    totalPoints: 0,

    generators: [],
    players: [],
    areas: [],
    currentAreaId: "",
    events: [],
    marketAssets: [],
    ownedGenerators: [],
    dailyPoints: [],
    weeklyPrizes: [],

    leagueLoadVersion: 0,
  }),

  getters: {
    currentAreaName(state) {
      const currentId = String(state.currentAreaId || "");

      if (!currentId) {
        return "Selecciona una liga";
      }

      const area = state.areas.find(
        item => String(item.id) === currentId
      );

      return area?.name || "Selecciona una liga";
    },
    hasActiveLeague(state) {
      const ligaId = normalizeLigaId(state.currentAreaId);

      if (!ligaId) {
        return false;
      }

      return state.areas.some(
        area => String(area.id) === String(ligaId)
      );
    },

    currentPlayerId(state) {
      return state.authUser?.id != null ? String(state.authUser.id) : null;
    },

    currentPlayerRank(state) {
      const sorted = [...state.players].sort(
        (a, b) => Number(b.points || 0) - Number(a.points || 0)
      );

      const me = state.authUser?.id != null ? String(state.authUser.id) : null;
      const index = sorted.findIndex((player) => String(player.id) === me);

      return index >= 0 ? index + 1 : 1;
    },

    totalPlayers(state) {
      return state.players.length;
    },

    availableBudget(state) {
      return Math.max(
        0,
        Number(state.budget || 0) - Number(state.reservedBudget || 0)
      );
    },
    availableSpendingCapacity(state) {
      return Math.max(
        0,
        Number(state.spendingCapacity || 0)
      );
    },
  },

  actions: {
   async deleteAccount() {
  this.error = null;

  try {
    const response = await axios.post(
      "/api/account/delete",
      {
        confirmation: "ELIMINAR",
      }
    );

    return jsendUnwrap(response);

  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudo eliminar la cuenta."
    );

    throw new Error(this.error);
  }
},
    async checkSystemStatus() {
  this.systemStatusChecking = true;

  try {
    const response = await axios.get("/api/system/status");
    const data = jsendUnwrap(response) || {};

    this.systemBlocked = Boolean(data.bloqueado);

    this.systemMessage =
      data.mensaje ||
      (this.systemBlocked
        ? "Calculando ganancias y resolviendo el mercado..."
        : null);

    this.systemProcess = data.proceso || null;

    return {
      blocked: this.systemBlocked,
      message: this.systemMessage,
      process: this.systemProcess,
    };
  } catch (error) {
    console.error(
      "Error comprobando el estado del sistema:",
      error?.response?.data || error
    );


    this.systemBlocked = false;
    this.systemMessage = null;
    this.systemProcess = null;

    return {
      blocked: false,
      message: null,
      process: null,
    };
  } finally {
    this.systemStatusChecking = false;
  }
},
    async deleteArea(areaId) {
  this.error = null;

  const ligaId = this.resolveLigaId(areaId);

  try {
    const response = await axios.delete(
      `/api/areas/${ligaId}`
    );

    const data = jsendUnwrap(response) || {};

    this.areas = Array.isArray(data.areas)
      ? data.areas
      : this.areas.filter(
          area => String(area.id) !== String(ligaId)
        );

    if (
      String(this.currentAreaId) ===
      String(ligaId)
    ) {
      this.currentAreaId = "";

      if (this.authUser) {
        this.authUser = {
          ...this.authUser,
          active_liga_id: null,
        };

        localStorage.setItem(
          "authUser",
          JSON.stringify(this.authUser)
        );
      }

      this.clearLeagueData();
    }

    this.activeTab = "areas";

    return data;
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudo eliminar la liga."
    );

    throw new Error(this.error);
  }
},
async fetchAreaParticipants(areaId) {
  this.error = null;

  const ligaId = this.resolveLigaId(areaId);

  try {
    const response = await axios.get(
      `/api/areas/${ligaId}/participants`
    );

    const data = jsendUnwrap(response);

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.participants)) {
      return data.participants;
    }

    return [];
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudieron cargar los participantes de la liga."
    );

    throw new Error(this.error);
  }
},

async removeAreaParticipant(areaId, userId) {
  this.error = null;

  const ligaId = this.resolveLigaId(areaId);
  const targetUserId = Number(userId);

  if (!Number.isInteger(targetUserId) || targetUserId <= 0) {
    throw new Error("El usuario seleccionado no es válido.");
  }

  try {
    const response = await axios.delete(
      `/api/areas/${ligaId}/participants/${targetUserId}`
    );

    const data = jsendUnwrap(response) || {};

    await Promise.all([
      this.loadAreas(),
      this.refreshRankings(ligaId),
    ]);

    return data;
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudo expulsar al jugador de la liga."
    );

    throw new Error(this.error);
  }
},
    async leaveArea(areaId = null) {
  this.error = null;

  const ligaId = Number(
    areaId || this.currentAreaId
  );

  try {
    const res = await axios.post(
      "/api/areas/leave",
      {
        liga_id: Number.isInteger(ligaId)
          ? ligaId
          : null,
      }
    );

    const data = jsendUnwrap(res);

    this.currentAreaId = "";

      if (this.authUser) {
        this.authUser = {
          ...this.authUser,
          active_liga_id: null,
          activeLigaId: null,
        };

        localStorage.setItem(
          "authUser",
          JSON.stringify(this.authUser)
        );
      }

      if (Array.isArray(data?.areas)) {
        this.areas = data.areas.map(area => ({
          ...area,
          isActive: false,
        }));
      } else {
        this.areas = this.areas.map(area => ({
          ...area,
          isActive: false,
        }));
      }

      this.clearLeagueData();
      this.activeTab = "areas";

    return data;
  } catch (e) {
    console.log(
      "ERROR leaveArea:",
      e?.response?.data || e
    );

    this.error =
      e?.response?.data?.message ||
      e?.response?.data?.data?.message ||
      e?.jsend?.message ||
      e?.message ||
      "No se pudo salir de la liga.";

    throw new Error(this.error);
  }
},
clearLeagueData() {
  this.budget = 0;
  this.reservedBudget = 0;
  this.portfolioValue = 0;
  this.creditLimit = 0;
  this.spendingCapacity = 0;
  this.inDebt = false;
  this.totalPoints = 0;
  this.currentDay = 0;
  this.totalDays = 0;

  this.generators = [];
  this.ownedGenerators = [];
  this.players = [];
  this.events = [];
  this.dailyPoints = [];
  this.weeklyPrizes = [];
  this.marketAssets = [];
},
    resolveLigaId(ligaId = null) {
      const resolved = normalizeLigaId(
        ligaId ??
          this.currentAreaId ??
          this.authUser?.active_liga_id ??
          this.authUser?.activeLigaId
      );

      if (!resolved) {
        throw new Error(
          "No se ha podido identificar la liga seleccionada. Vuelve a entrar en la liga."
        );
      }

      return resolved;
    },

    setCurrentLigaLocal(ligaId) {
      const resolved = this.resolveLigaId(ligaId);
      this.currentAreaId = String(resolved);

      if (this.authUser) {
        this.authUser = {
          ...this.authUser,
          active_liga_id: resolved,
          activeLigaId: resolved,
        };

        localStorage.setItem("authUser", JSON.stringify(this.authUser));
      }

      this.areas = this.areas.map((area) => ({
        ...area,
        isActive: String(area.id) === String(resolved),
      }));

      return resolved;
    },

    

    async loadAreas() {
      const response = await axios.get("/api/areas");
      const areas = jsendUnwrap(response);

      this.areas = Array.isArray(areas) ? areas : [];

      const active = this.areas.find(
        area => area.isActive
      );

      const ligaId = normalizeLigaId(active?.id);

      if (ligaId) {
        this.setCurrentLigaLocal(ligaId);
      } else {
        this.currentAreaId = "";

        if (this.authUser) {
          this.authUser = {
            ...this.authUser,
            active_liga_id: null,
            activeLigaId: null,
          };

          localStorage.setItem(
            "authUser",
            JSON.stringify(this.authUser)
          );
        }

        this.clearLeagueData();
        this.activeTab = "areas";
      }

      return this.areas;
    },

    async loadAll() {
  if (!this.authToken) return;

  this.loading = true;
  this.error = null;

  try {
   
    const systemStatus = await this.checkSystemStatus();

    if (systemStatus.blocked) {
      console.log(
        "[SYSTEM] Juego temporalmente bloqueado:",
        systemStatus.process,
        systemStatus.message
      );

      
      return {
        blocked: true,
      };
    }

    await this.loadAreas();

    if (!this.currentAreaId) {
      this.activeTab = "areas";
      return;
    }

    await this.loadLeagueData(this.currentAreaId, {
      showNotifications: true,
    });

    return {
      blocked: false,
    };
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudieron cargar los datos del juego."
    );

    throw error;
  } finally {
    this.loading = false;
  }
},

    async loadLeagueData(ligaId = null, options = {}) {
      const resolvedLigaId = this.resolveLigaId(ligaId);
      const requestVersion = ++this.leagueLoadVersion;
      const params = { liga_id: resolvedLigaId };

      const [
        statsRes,
        genRes,
        rankRes,
        eventsRes,
        dailyPointsRes,
        weeklyPrizesRes,
        companyRes,
      ] = await Promise.all([
        axios.get("/api/game/stats", { params }),
        axios.get("/api/market/generators", { params }),
        axios.get("/api/rankings", { params }),
        axios.get("/api/activity/events", { params }),
        axios.get("/api/activity/daily-points", { params }),
        axios.get("/api/activity/weekly-prizes", { params }),
        axios.get("/api/company", { params }),
      ]);

      if (
        requestVersion !== this.leagueLoadVersion ||
        String(this.currentAreaId) !== String(resolvedLigaId)
      ) {
        return null;
      }

      const stats = jsendUnwrap(statsRes) || {};
      const generators = jsendUnwrap(genRes);
      const players = jsendUnwrap(rankRes);
      const events = jsendUnwrap(eventsRes);
      const dailyPoints = jsendUnwrap(dailyPointsRes);
      const weeklyPrizes = jsendUnwrap(weeklyPrizesRes);
      const company = jsendUnwrap(companyRes) || {};

      this.currentDay = Number(stats.currentDay || 0);
      this.totalDays = Number(stats.totalDays || 0);
      this.totalPoints = Number(stats.points || 0);
      this.budget = Number(stats.budget || 0);
      this.reservedBudget = Number(stats.reserved_budget || 0);

      this.generators = Array.isArray(generators) ? generators : [];
      this.players = Array.isArray(players) ? players : [];
      this.events = Array.isArray(events) ? events : [];
      this.dailyPoints = Array.isArray(dailyPoints) ? dailyPoints : [];
      this.weeklyPrizes = Array.isArray(weeklyPrizes) ? weeklyPrizes : [];
      this.ownedGenerators = Array.isArray(company.ownedGenerators)
        ? company.ownedGenerators
        : [];

      const player = company.player || {};

      if (player.budget != null) {
        this.budget = Number(player.budget);
      }

      if (player.points != null) {
        this.totalPoints = Number(player.points);
      }

      if (player.reserved_budget != null) {
        this.reservedBudget = Number(player.reserved_budget);
      }
      if (player.valor_plantilla != null) {
        this.portfolioValue = Number(player.valor_plantilla);
      }

      if (player.limite_credito != null) {
        this.creditLimit = Number(player.limite_credito);
      }

      if (player.capacidad_gasto != null) {
        this.spendingCapacity = Number(player.capacidad_gasto);
      }

      if (player.en_deuda != null) {
        this.inDebt = Boolean(player.en_deuda);
      }

      this.marketAssets = [];

      if (options.showNotifications !== false) {
        await this.showPendingNotifications(resolvedLigaId);
      }

      return {
        ligaId: resolvedLigaId,
        stats,
        company,
      };
    },

    async enterArea(areaId, targetTab = "rankings") {
      return this.selectArea(areaId, {
        loadData: true,
        targetTab,
      });
    },

    async selectArea(areaId, options = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(areaId);

      try {
        const response = await axios.post("/api/areas/select", {
          areaId: ligaId,
          liga_id: ligaId,
        });

        const data = jsendUnwrap(response) || {};
        const selectedLigaId = this.resolveLigaId(
          data.currentAreaId ?? data.ligaId ?? ligaId
        );

        if (Array.isArray(data.areas)) {
          this.areas = data.areas;
        }

        this.setCurrentLigaLocal(selectedLigaId);

        if (options.loadData !== false) {
          this.loading = true;
          await this.loadLeagueData(selectedLigaId, {
            showNotifications: true,
          });
        }

        if (options.targetTab) {
          this.activeTab = options.targetTab;
        }

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo seleccionar la liga.");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async joinArea(areaId) {
      this.error = null;
      const ligaId = this.resolveLigaId(areaId);

      try {
        const response = await axios.post("/api/areas/join", {
          areaId: ligaId,
          liga_id: ligaId,
        });

        const data = jsendUnwrap(response) || {};
        await this.selectArea(data.currentAreaId ?? data.ligaId ?? ligaId, {
          loadData: true,
          targetTab: "rankings",
        });

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo entrar en la liga.");
        throw error;
      }
    },

   async createArea(payload = {}) {
      this.error = null;

      const normalizedPayload =
        typeof payload === "string"
          ? { name: payload }
          : payload;

      const name = String(
        normalizedPayload.name || ""
      ).trim();

      const durationDays = Number(
        normalizedPayload.durationDays ?? 60
      );

      try {
        const response = await axios.post(
          "/api/areas/create",
          {
            name,
            durationDays,
          }
        );

        const data = jsendUnwrap(response) || {};

        const ligaId = this.resolveLigaId(
          data.currentAreaId ??
          data.ligaId ??
          data.id
        );

        if (Array.isArray(data.areas)) {
          this.areas = data.areas;
        }

        this.setCurrentLigaLocal(ligaId);

        await this.loadLeagueData(
          ligaId,
          {
            showNotifications: false,
          }
        );

        const codigo =
          data.code ||
          data.codigo ||
          "";
        const minPlayers = Number(
          data.minPlayers ?? 3
        );

        await Swal.fire({
          icon: "success",
          title: "Liga creada",

          html: `
            <div style="text-align:center">
              <p>La liga se ha creado correctamente.</p>

              <p>
                Duración:
                <strong>${durationDays} días</strong>
              </p>

              <p>
                Mínimo:
                <strong>${minPlayers} jugadores</strong>
              </p>

              <p>Código para compartir:</p>

              <h2 style="letter-spacing:2px">
                ${codigo}
              </h2>
            </div>
          `,

          confirmButtonText: "Entendido",

          buttonsStyling: false,

          customClass: {
            popup: "liga-creada-popup",
            confirmButton:
              "liga-creada-confirmar",
          },
        });

        this.activeTab = "rankings";

        return data;

      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo crear la liga."
        );

        await Swal.fire({
          icon: "error",
          title: "Error",
          text: this.error,
          confirmButtonText: "Entendido",

          customClass: {
            confirmButton:
              "clausulazo-swal-confirm",
          },
        });

        throw error;
      }
    },
   async startArea(areaId, durationDays) {
  this.error = null;

  const ligaId = this.resolveLigaId(areaId);

  const normalizedDurationDays = Number(
    durationDays
  );

  if (
    !Number.isInteger(normalizedDurationDays) ||
    normalizedDurationDays < 1
  ) {
    throw new Error(
      "La duración de la liga no es válida."
    );
  }

  try {
    const response = await axios.post(
      `/api/areas/${ligaId}/start`,
      {
        durationDays:
          normalizedDurationDays,
      }
    );

    const data =
      jsendUnwrap(response) || {};

    if (Array.isArray(data.areas)) {
      this.areas = data.areas;
    } else {
      await this.loadAreas();
    }

    return data;
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudo iniciar la liga."
    );

    throw error;
  }
},

    async joinAreaByCode(code) {
      this.error = null;

      try {
        const response = await axios.post("/api/areas/join-code", { code });
        const data = jsendUnwrap(response) || {};
        const ligaId = this.resolveLigaId(
          data.currentAreaId ?? data.ligaId ?? data.id
        );

        if (Array.isArray(data.areas)) {
          this.areas = data.areas;
        }

        this.setCurrentLigaLocal(ligaId);
        await this.loadLeagueData(ligaId, { showNotifications: false });

        await Swal.fire({
          icon: "success",
          title: "Te has unido a la liga",
          text: "Ya puedes competir en esta liga.",
          confirmButtonText: "Entendido",
          customClass: {
            confirmButton: "clausulazo-swal-confirm",
          },
        });

        this.activeTab = "rankings";
        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo unir a la liga.");

        await Swal.fire({
          icon: "error",
          title: "Error",
          text: this.error,
          confirmButtonText: "Entendido",
          customClass: {
            confirmButton: "clausulazo-swal-confirm",
          },
        });

        throw error;
      }
    },

    async showPendingNotifications(ligaId = null) {
      const resolvedLigaId = this.resolveLigaId(ligaId);
      const notifications = await this.fetchUnreadNotifications(resolvedLigaId);

      if (!notifications.length) return;

      const readIds = [];

      const formatMoney = (value) =>
        new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(Number(value || 0));

      for (const notification of notifications) {
        const isClausulazo =
          notification.type === "clausulazo_recibido";

        const isOfertaAceptada =
          notification.type === "oferta_aceptada";

        const isOfertaRecibida =
          notification.type === "oferta_recibida";

        const isOfertaMercado =
          notification.type === "oferta_mercado";

        const isPujaGanada =
          notification.type === "puja_ganada";

        const isPujaPerdida =
          notification.type === "puja_perdida";

        let notificationData = notification.data || {};

        if (typeof notificationData === "string") {
          try {
            notificationData = JSON.parse(notificationData);
          } catch (error) {
            console.error(
              "No se pudo leer notification.data:",
              error
            );
            notificationData = {};
          }
        }

        let mensajeVisible = notification.message || "";

        // =====================================================
        // CLAUSULAZO
        // =====================================================
        if (
          isClausulazo &&
          notificationData.precio_clausulazo !== undefined
        ) {
          const comprador =
            notificationData.comprador_username ||
            "Otro jugador";

          const activo =
            notificationData.activo_nombre ||
            "el activo";

          const precio = formatMoney(
            notificationData.precio_clausulazo
          );

          mensajeVisible =
            `${comprador} te ha quitado ${activo} ` +
            `pagando ${precio}.`;

          const numBaterias = Number(
            notificationData.num_baterias_activas || 0
          );

          const valorBaterias = Number(
            notificationData.valor_baterias || 0
          );

          if (numBaterias > 0) {
            mensajeVisible +=
              ` Incluye ${numBaterias} ` +
              `${numBaterias === 1
                ? "batería activa"
                : "baterías activas"} ` +
              `valorada${numBaterias === 1 ? "" : "s"} en ` +
              `${formatMoney(valorBaterias)}.`;
          }
        }

        // =====================================================
        // OFERTA RECIBIDA DE OTRO JUGADOR
        // =====================================================
        if (isOfertaRecibida) {
          const comprador =
            notificationData.comprador_nombre ||
            "Otro jugador";

          const activo =
            notificationData.activo_nombre ||
            "el activo";

          const importe = formatMoney(
            notificationData.importe
          );

          const importeAnterior = Number(
            notificationData.importe_anterior || 0
          );

          if (importeAnterior > 0) {
            mensajeVisible =
              `${comprador} ha modificado su oferta por ` +
              `${activo}. Ahora ofrece ${importe}.`;
          } else {
            mensajeVisible =
              `${comprador} ha hecho una oferta de ` +
              `${importe} por ${activo}.`;
          }
        }

        // =====================================================
        // OFERTA DEL MERCADO
        // =====================================================
        if (isOfertaMercado) {
          const activo =
            notificationData.activo_nombre ||
            "el activo";

          const precio = formatMoney(
            notificationData.precio_banca
          );

          mensajeVisible =
            `La venta de ${activo} ha finalizado. ` +
            `El mercado te ofrece ${precio}. ` +
            `Tienes 24 horas para aceptar la venta al mercado.`;
        }

        // =====================================================
        // PUJA GANADA
        // =====================================================
        if (isPujaGanada) {
          const activo =
            notificationData.activo_nombre ||
            "el activo";

          const importe = formatMoney(
            notificationData.importe
          );

          mensajeVisible =
            `Has ganado la puja por ${activo} ` +
            `con una oferta de ${importe}.`;
        }

        // =====================================================
        // PUJA PERDIDA
        // =====================================================
        if (isPujaPerdida) {
          const activo =
            notificationData.activo_nombre ||
            "el activo";

          const miPuja = formatMoney(
            notificationData.mi_puja
          );

          const motivo =
            notificationData.motivo || "";

          if (
            motivo === "otra_puja_ganadora" &&
            notificationData.puja_ganadora !== undefined
          ) {
            const pujaGanadora = formatMoney(
              notificationData.puja_ganadora
            );

            mensajeVisible =
              `No has ganado la puja por ${activo}. ` +
              `Tu oferta era de ${miPuja} y la oferta ` +
              `ganadora ha sido de ${pujaGanadora}. ` +
              `El dinero reservado vuelve a estar disponible.`;
          } else if (motivo === "activo_no_disponible") {
            mensajeVisible =
              `No has podido obtener ${activo}. ` +
              `El activo ya no estaba disponible cuando ` +
              `se resolvió el mercado. ` +
              `Los ${miPuja} reservados vuelven a estar disponibles.`;
          } else {
            mensajeVisible =
              `La puja por ${activo} no ha podido adjudicarse. ` +
              `Tu puja era de ${miPuja}. ` +
              `El dinero reservado vuelve a estar disponible.`;
          }
        }

        // =====================================================
        // ICONO
        // =====================================================
        let icon = "info";

        if (isClausulazo) {
          icon = "warning";
        } else if (
          isOfertaAceptada ||
          isPujaGanada
        ) {
          icon = "success";
        } else if (isPujaPerdida) {
          icon = "warning";
        } else if (
          isOfertaRecibida ||
          isOfertaMercado
        ) {
          icon = "info";
        }

        // =====================================================
        // TEXTO EXTRA
        // =====================================================
        let textoExtra = "";

        if (isClausulazo) {
          textoExtra =
            "<p><b>El activo ya no está en tu empresa.</b></p>";
        } else if (isOfertaAceptada) {
          textoExtra =
            "<p><b>El activo ya está en tu plantilla.</b></p>";
        } else if (isPujaGanada) {
          textoExtra =
            "<p><b>El activo ya está disponible en tu empresa.</b></p>";
        } else if (isOfertaRecibida) {
          textoExtra =
            "<p><b>Puedes revisar la oferta en el mercado de ventas.</b></p>";
        } else if (isOfertaMercado) {
          textoExtra =
            "<p><b>Puedes aceptar la oferta desde el mercado de ventas.</b></p>";
        }

        // =====================================================
        // MOSTRAR NOTIFICACIÓN
        // =====================================================
        const result = await Swal.fire({
          icon,
          title:
            notification.title ||
            "Notificación",

          html: `
            <div style="text-align:left">
              <p>${mensajeVisible}</p>
              ${textoExtra}
            </div>
          `,

          confirmButtonText: "Entendido",

          customClass: {
            confirmButton:
              "clausulazo-swal-confirm",
          },

          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (result.isConfirmed) {
          readIds.push(notification.id);
        }
      }

      if (readIds.length) {
        await this.markNotificationsRead(
          readIds,
          resolvedLigaId
        );
      }
    },

    async fetchUnreadNotifications(ligaId = null) {
      try {
        const resolvedLigaId = this.resolveLigaId(ligaId);
        const response = await axios.get("/api/notifications/unread", {
          params: { liga_id: resolvedLigaId },
        });
        const data = jsendUnwrap(response);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.log(
          "ERROR fetchUnreadNotifications:",
          error?.response?.data || error
        );
        return [];
      }
    },

    async markNotificationsRead(ids, ligaId = null) {
      try {
        const resolvedLigaId = this.resolveLigaId(ligaId);
        const response = await axios.post("/api/notifications/mark-read", {
          ids,
          liga_id: resolvedLigaId,
        });
        return jsendUnwrap(response);
      } catch (error) {
        console.log(
          "ERROR markNotificationsRead:",
          error?.response?.data || error
        );
        return null;
      }
    },

    async refreshStats(ligaId = null) {
      if (!this.authToken) return null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/game/stats", {
          params: { liga_id: resolvedLigaId },
        });
        const stats = jsendUnwrap(response) || {};

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.currentDay = Number(stats.currentDay || 0);
          this.totalDays = Number(stats.totalDays || 0);
          this.totalPoints = Number(stats.points || 0);
          this.budget = Number(stats.budget || 0);
          this.reservedBudget = Number(stats.reserved_budget || 0);
        }

        return stats;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudieron actualizar los datos.");
        throw error;
      }
    },

    async refreshCompany(ligaId = null) {
      if (!this.authToken) return null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/company", {
          params: { liga_id: resolvedLigaId },
        });
        const company = jsendUnwrap(response) || {};

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.ownedGenerators = Array.isArray(company.ownedGenerators)
            ? company.ownedGenerators
            : [];

          const player = company.player || {};

          if (player.budget != null) {
            this.budget = Number(player.budget);
          }

          if (player.points != null) {
            this.totalPoints = Number(player.points);
          }

          if (player.reserved_budget != null) {
            this.reservedBudget = Number(player.reserved_budget);
          }

          if (player.valor_plantilla != null) {
            this.portfolioValue = Number(player.valor_plantilla);
          }

          if (player.limite_credito != null) {
            this.creditLimit = Number(player.limite_credito);
          }

          if (player.capacidad_gasto != null) {
            this.spendingCapacity = Number(player.capacidad_gasto);
          }

          if (player.en_deuda != null) {
            this.inDebt = Boolean(player.en_deuda);
          }
        }

        return company;
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo actualizar la plantilla."
        );
        throw error;
      }
    },

    async refreshGenerators(ligaId = null) {
      if (!this.authToken) return [];
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/market/generators", {
          params: { liga_id: resolvedLigaId },
        });
        const generators = jsendUnwrap(response);

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.generators = Array.isArray(generators) ? generators : [];
        }

        return Array.isArray(generators) ? generators : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo actualizar el mercado."
        );
        throw error;
      }
    },

    async refreshRankings(ligaId = null) {
      if (!this.authToken) return [];
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/rankings", {
          params: { liga_id: resolvedLigaId },
        });
        const players = jsendUnwrap(response);

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.players = Array.isArray(players) ? players : [];
        }

        return Array.isArray(players) ? players : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo actualizar la clasificación."
        );
        throw error;
      }
    },

    async refreshEvents(ligaId = null) {
      if (!this.authToken) return [];
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/activity/events", {
          params: { liga_id: resolvedLigaId },
        });
        const events = jsendUnwrap(response);

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.events = Array.isArray(events) ? events : [];
        }

        return Array.isArray(events) ? events : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo actualizar la actividad."
        );
        throw error;
      }
    },

    async refreshDailyPoints(ligaId = null) {
      if (!this.authToken) return [];
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/activity/daily-points", {
          params: { liga_id: resolvedLigaId },
        });
        const dailyPoints = jsendUnwrap(response);

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.dailyPoints = Array.isArray(dailyPoints) ? dailyPoints : [];
        }

        return Array.isArray(dailyPoints) ? dailyPoints : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo actualizar el resumen de puntos diarios."
        );
        throw error;
      }
    },

    async refreshWeeklyPrizes(ligaId = null) {
      if (!this.authToken) return [];
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/activity/weekly-prizes", {
          params: { liga_id: resolvedLigaId },
        });
        const weeklyPrizes = jsendUnwrap(response);

        if (String(this.currentAreaId) === String(resolvedLigaId)) {
          this.weeklyPrizes = Array.isArray(weeklyPrizes)
            ? weeklyPrizes
            : [];
        }

        return Array.isArray(weeklyPrizes) ? weeklyPrizes : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo actualizar el resumen de premios semanales."
        );
        throw error;
      }
    },

    async clausulazoActivo(assetId, ligaId = null) {
      this.error = null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.post(
          `/api/market/clausulazo/${assetId}`,
          { liga_id: resolvedLigaId }
        );
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshCompany(resolvedLigaId),
          this.refreshGenerators(resolvedLigaId),
          this.refreshEvents(resolvedLigaId),
          this.refreshStats(resolvedLigaId),
          this.refreshRankings(resolvedLigaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo hacer el clausulazo."
        );
        throw new Error(this.error);
      }
    },

    async createSale(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/anadir-venta", {
          kind: payload.assetType,
          asset_id: payload.id,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshEvents(ligaId),
          this.refreshGenerators(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo crear la venta.");
        throw error;
      }
    },

    async buyGenerator(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/buy", {
          ...payload,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        if (data?.budget != null) this.budget = Number(data.budget);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshEvents(ligaId),
          this.refreshGenerators(ligaId),
          this.refreshStats(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo comprar el activo.");
        throw error;
      }
    },

    async bidGenerator(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/anadir-mercado", {
          kind: payload.assetType,
          asset_id: payload.id,
          value: payload.amount,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshGenerators(ligaId),
          this.refreshEvents(ligaId),
          this.refreshCompany(ligaId),
          this.refreshStats(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo registrar la puja.");
        throw error;
      }
    },

    async cancelBidGenerator(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/cancelar-puja", {
          asset_id: payload.id,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshGenerators(ligaId),
          this.refreshCompany(ligaId),
          this.refreshEvents(ligaId),
          this.refreshStats(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo cancelar la puja.");
        throw error;
      }
    },

    async sellGenerator(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/sell", {
          ...payload,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshEvents(ligaId),
          this.refreshStats(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo vender el activo.");
        throw error;
      }
    },

    async sellOwnedGenerator(generator, explicitLigaId = null) {
      this.error = null;
      const generatorId = generator?.id || generator;
      const ligaId = this.resolveLigaId(
        explicitLigaId ?? generator?.liga_id ?? generator?.ligaId
      );

      try {
        const response = await axios.post(
          "/api/market/liquidacion-directa",
          {
            assetType: "generator",
            id: generatorId,
            liga_id: ligaId,
          }
        );
        const data = jsendUnwrap(response);

        if (data?.budget != null) this.budget = Number(data.budget);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshEvents(ligaId),
          this.refreshStats(ligaId),
          this.refreshGenerators(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo vender el activo.");
        throw error;
      }
    },

    async createSaleFromCompany(generatorOrId, explicitLigaId = null) {
      const generatorId = generatorOrId?.id || generatorOrId;
      const ligaId = this.resolveLigaId(
        explicitLigaId ??
          generatorOrId?.liga_id ??
          generatorOrId?.ligaId
      );

      return this.createSale({
        assetType: "generator",
        id: generatorId,
        liga_id: ligaId,
      });
    },

    async createOffer(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/anadir-oferta", {
          sale_id: payload.sale_id,
          value: payload.amount,
          liga_id: ligaId,
        });
        return jsendUnwrap(response);
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo crear la oferta.");
        throw error;
      }
    },

    async acceptOffer(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/market/confirmar-oferta", {
          offer_id: payload.offer_id,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshGenerators(ligaId),
          this.refreshEvents(ligaId),
          this.refreshStats(ligaId),
          this.refreshRankings(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo aceptar la oferta.");
        throw error;
      }
    },

    async cancelSale(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post(
          "/api/market/cancelar-oferta-venta",
          {
            sale_id: payload.sale_id,
            liga_id: ligaId,
          }
        );
        const data = jsendUnwrap(response);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshGenerators(ligaId),
          this.refreshEvents(ligaId),
          this.refreshStats(ligaId),
          this.refreshRankings(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(error, "No se pudo cancelar la venta.");
        throw error;
      }
    },
async fetchLastSeasonResult(ligaId = null) {
  this.error = null;

  const resolvedLigaId =
    this.resolveLigaId(ligaId);

  try {
    const response = await axios.get(
      "/api/rankings/last-season",
      {
        params: {
          liga_id: resolvedLigaId,
        },
      }
    );

    const data =
      jsendUnwrap(response) || {};

    return data;
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudo cargar la clasificación final."
    );

    throw new Error(this.error);
  }
},
    async fetchPlayerAssets(playerId, ligaId = null) {
      this.error = null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get("/api/company_user", {
          params: {
            user_id: playerId,
            liga_id: resolvedLigaId,
          },
        });
        const data = jsendUnwrap(response) || {};
        return Array.isArray(data.ownedGenerators) ? data.ownedGenerators : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudieron cargar los activos del jugador."
        );
        throw error;
      }
    },

    async fetchAssetHistory(assetId, ligaId = null) {
      this.error = null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get(
          `/api/rankings/assets/${assetId}/history`,
          { params: { liga_id: resolvedLigaId } }
        );
        const data = jsendUnwrap(response);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo cargar el histórico del activo."
        );
        throw new Error(this.error);
      }
    },
    async fetchMyAssetHistory(assetId, ligaId = null) {
  this.error = null;
  const resolvedLigaId = this.resolveLigaId(ligaId);

  try {
    const response = await axios.get(
      `/api/rankings/assets/${assetId}/my-history`,
      {
        params: {
          liga_id: resolvedLigaId,
        },
      }
    );

    const data = jsendUnwrap(response);

    return Array.isArray(data) ? data : [];
  } catch (error) {
    this.error = getErrorMessage(
      error,
      "No se pudo cargar el histórico del activo."
    );

    throw new Error(this.error);
  }
},

    async fetchClausulazoPrice(assetId, ligaId = null) {
      this.error = null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.get(
          `/api/market/clausulazo/${assetId}/precio`,
          { params: { liga_id: resolvedLigaId } }
        );
        return jsendUnwrap(response) || null;
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo calcular el valor de clausulazo."
        );
        throw new Error(this.error);
      }
    },

    async toggleAssetOperativo(assetId, operativo, ligaId = null) {
      this.error = null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.post("/api/company/toggle_operativo", {
          asset_id: assetId,
          operativo,
          liga_id: resolvedLigaId,
        });
        const data = jsendUnwrap(response);
        await this.refreshCompany(resolvedLigaId);
        return data;
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo cambiar el estado operativo."
        );
        throw error;
      }
    },

    async buyBattery(payload = {}) {
      this.error = null;
      const ligaId = this.resolveLigaId(payload.liga_id ?? payload.ligaId);

      try {
        const response = await axios.post("/api/company/batteries/buy", {
          ...payload,
          liga_id: ligaId,
        });
        const data = jsendUnwrap(response);

        if (data?.budget != null) this.budget = Number(data.budget);

        await Promise.all([
          this.refreshCompany(ligaId),
          this.refreshStats(ligaId),
          this.refreshEvents(ligaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo comprar la batería."
        );
        throw new Error(this.error);
      }
    },

    async revampBattery(batteryId, ligaId = null) {
      this.error = null;
      const resolvedLigaId = this.resolveLigaId(ligaId);

      try {
        const response = await axios.post("/api/company/batteries/revamp", {
          battery_id: batteryId,
          liga_id: resolvedLigaId,
        });
        const data = jsendUnwrap(response);

        if (data?.budget != null) this.budget = Number(data.budget);

        await Promise.all([
          this.refreshCompany(resolvedLigaId),
          this.refreshStats(resolvedLigaId),
          this.refreshEvents(resolvedLigaId),
        ]);

        return data;
      } catch (error) {
        this.error = getErrorMessage(
          error,
          "No se pudo hacer el revamping de la batería."
        );
        throw new Error(this.error);
      }
    },

    setAuth(token, user) {
      this.authToken = token;
      this.authUser = user;

      localStorage.setItem("authToken", token);
      localStorage.setItem("authUser", JSON.stringify(user));
    },

    loadAuthFromStorage() {
      const token = localStorage.getItem("authToken");
      const userRaw = localStorage.getItem("authUser");

      if (!token) return;

      this.authToken = token;
      this.authUser = userRaw ? JSON.parse(userRaw) : null;

      const ligaId = normalizeLigaId(
        this.authUser?.active_liga_id ?? this.authUser?.activeLigaId
      );

      if (ligaId) {
        this.currentAreaId = String(ligaId);
      }
    },

    logout() {
      this.leagueLoadVersion += 1;
      this.authToken = null;
      this.authUser = null;
      this.currentAreaId = "";
      this.areas = [];
      this.clearLeagueData();
      this.activeTab = "areas";
      this.error = null;

      // Limpiar estado del bloqueo del sistema
      this.systemBlocked = false;
      this.systemMessage = null;
      this.systemProcess = null;
      this.systemStatusChecking = false;

      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
    },
  },
});
